import { useEffect, useMemo } from 'react';
import { TaskCenterQueries, TaskCenterTypesV2 } from '@frontend/api-task-center';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useAppScopeStore } from '@frontend/scope';
import { CitationMetadata } from '@frontend/task-center-scope';

interface UseFilterPrivateTasksParams {
  filters: TaskCenterTypesV2.SchemaTypes.ListTasksRequestV2['filters'];
  pagination?: TaskCenterTypesV2.SchemaTypes.ListTasksRequestV2['pagination'];
  enabled: boolean;
}

export const useFilterPrivateTasks = ({ filters, pagination = {}, enabled }: UseFilterPrivateTasksParams) => {
  const { selectedLocationIds } = useAppScopeStore();

  const taskQuery = TaskCenterQueries.useGetTasksListV2({ filters, pagination }, { enabled });
  const checkPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);

  const filteredTaskQueryData = useMemo(() => {
    if (!taskQuery.data) return taskQuery.data;
    const pages = taskQuery.data.pages.map((page) => {
      if (!page.tasks) return page;
      const filteredTasks = page?.tasks.filter((task) => {
        const taskMetadata: CitationMetadata = task?.metadata ? JSON.parse(task.metadata) : undefined;
        if (!taskMetadata?.source?.locationPhone) return true;
        return checkPhoneAccess(taskMetadata.source.locationPhone);
      });
      return { ...page, tasks: filteredTasks };
    });
    return { ...taskQuery.data, pages };
  }, [checkPhoneAccess, taskQuery.data]);

  const list = useMemo(() => {
    return filteredTaskQueryData?.pages?.flatMap((page) => page.tasks).filter((task) => !!task);
  }, [filteredTaskQueryData]);

  const lastPageLength = !!filteredTaskQueryData?.pages?.length
    ? filteredTaskQueryData.pages[filteredTaskQueryData.pages.length - 1]?.tasks?.length || 0
    : 0;

  // Auto-fetch pages until filtered results exist or no more pages
  useEffect(() => {
    if (taskQuery.isLoading || taskQuery.isFetchingNextPage) return; // avoid duplicate fetches
    if (lastPageLength) return; // we have data → stop fetching more
    if (!taskQuery.hasNextPage) return; // no more pages → stop

    taskQuery.fetchNextPage();
  }, [
    taskQuery.hasNextPage,
    taskQuery.fetchNextPage,
    taskQuery.isLoading,
    taskQuery.isFetchingNextPage,
    lastPageLength,
  ]);

  const derivedLoading = taskQuery.isLoading || (lastPageLength === 0 && taskQuery.hasNextPage);

  return {
    ...taskQuery,
    list,
    ...{ data: filteredTaskQueryData, isLoading: derivedLoading },
  };
};
